import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Course } from "../../types/course.types";
import {
  courseApi,
  getThumbnailUrl,
  countPublishedLessons,
  flattenPublishedLessons,
} from "../../services/course.service";
import { useT } from "../../i18n";

const levelBadge: Record<string, string> = {
  beginner: "bg-green-100 text-green-800",
  intermediate: "bg-yellow-100 text-yellow-800",
  advanced: "bg-orange-100 text-orange-800",
  certification: "bg-purple-100 text-purple-800",
};

const CourseCard: React.FC<{
  course: Course;
  percent: number;
  onOpen: (id: string) => void;
}> = ({ course, percent, onOpen }) => {
  const t = useT();
  const first = flattenPublishedLessons(course)[0];
  const thumb =
    course.thumbnailUrl ||
    (first ? getThumbnailUrl(first.videoUrl, first.source) : null);
  const lessonCount = countPublishedLessons(course);
  const started = percent > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow flex flex-col">
      <div className="relative w-full h-44 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <i className="fa-solid fa-graduation-cap text-4xl text-white/30"></i>
        )}
        <span
          className={`absolute top-3 right-3 px-2 py-1 text-xs rounded-full capitalize ${
            levelBadge[course.level] || "bg-gray-100 text-gray-700"
          }`}
        >
          {course.level}
        </span>
        {percent === 100 && (
          <span className="absolute top-3 left-3 px-2 py-1 text-xs rounded-full bg-green-600 text-white font-semibold">
            <i className="fa-solid fa-check mr-1"></i>{t("academy.status.completed")}
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <span className="text-xs uppercase tracking-wide text-gray-400">
          {course.category}
        </span>
        <h4 className="text-lg font-semibold text-gray-800 mt-0.5 mb-2 line-clamp-1">
          {course.title}
        </h4>
        {course.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {course.description}
          </p>
        )}

        <div className="flex items-center mb-3 text-xs text-gray-500 gap-3">
          <span>
            <i className="fa-solid fa-list-check mr-1"></i>
            {t(lessonCount === 1 ? "academy.lessonCountOne" : "academy.lessonCount", { count: lessonCount })}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{started ? t("academy.status.inProgress") : t("academy.status.notStarted")}</span>
            <span>{percent}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-red-600 rounded-full transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <button
          onClick={() => onOpen(course.id)}
          className="mt-auto w-full px-4 py-2 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
        >
          <i className="fa-solid fa-play mr-2"></i>
          {percent === 100 ? t("academy.cta.review") : started ? t("academy.cta.continue") : t("academy.cta.start")}
        </button>
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse">
    <div className="h-44 bg-gray-200" />
    <div className="p-6 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-8 bg-gray-100 rounded w-full mt-2" />
    </div>
  </div>
);

const AcademyPage: React.FC = () => {
  const navigate = useNavigate();
  const t = useT();
  const [courses, setCourses] = useState<Course[]>([]);
  const [percentByCourse, setPercentByCourse] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await courseApi.getAllCourses();
        const list = res.data || [];
        if (!active) return;
        setCourses(list);
        setLoading(false);

        // Fetch progress per course (after render) to compute %.
        const entries = await Promise.allSettled(
          list.map(async (c) => {
            const p = await courseApi.getProgress(c.id);
            let pct = p.data?.percentComplete;
            if (typeof pct !== "number") {
              // Fallback: compute from completed ids over published lessons.
              const total = countPublishedLessons(c);
              const completedSet = new Set(p.data?.completedLessonIds || []);
              const done = flattenPublishedLessons(c).filter((l) =>
                completedSet.has(l.id)
              ).length;
              pct = total === 0 ? 0 : Math.round((done / total) * 100);
            }
            return [c.id, pct] as const;
          })
        );
        if (!active) return;
        const map: Record<string, number> = {};
        entries.forEach((e) => {
          if (e.status === "fulfilled") map[e.value[0]] = e.value[1];
        });
        setPercentByCourse(map);
      } catch (err: any) {
        if (active) {
          setError(
            t("academy.loadFailed")
          );
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const grouped: Record<string, Course[]> = {};
  courses.forEach((c) => {
    (grouped[c.category] = grouped[c.category] || []).push(c);
  });

  return (
    <div className="flex flex-col flex-1 h-full">
      <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
        <h1 className="text-2xl font-bold text-gray-900">{t("academy.title")}</h1>
        <p className="text-sm text-gray-500 max-w-2xl">
          {t("academy.subtitle")}
        </p>
      </div>

      <div className="flex-1 px-8 pb-8">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-4">
          <div className="p-8">
            <p className="text-gray-700 leading-relaxed mb-8">
              {t("academy.intro")}
            </p>

            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            {!loading && !error && courses.length === 0 && (
              <div className="text-center py-12">
                <i className="fa-solid fa-graduation-cap text-3xl text-gray-300"></i>
                <p className="mt-3 text-sm text-gray-500">
                  {t("academy.empty")}
                </p>
              </div>
            )}

            {!loading &&
              !error &&
              Object.entries(grouped).map(([category, list]) => (
                <div key={category} className="mb-12 last:mb-0">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <i className="fa-solid fa-graduation-cap text-red-600 mr-2"></i>
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map((c) => (
                      <CourseCard
                        key={c.id}
                        course={c}
                        percent={percentByCourse[c.id] ?? 0}
                        onOpen={(id) => navigate(`/academy/${id}`)}
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademyPage;
